import {
  ExternalLink, FileText, Globe, Trash2,
} from 'lucide-react';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const Chip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  min-width: 0;
  height: 40px;
  padding: 0 6px 0 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#e5e7eb')};
  background: ${({ theme }) => (theme.mode === 'dark' ? '#1f2937' : '#ffffff')};
`;

const Name = styled.a`
  min-width: 0;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#e5e7eb' : '#1f2937')};
  text-decoration: none;

  &:hover { text-decoration: underline; }
`;

const IconButton = styled.button<{ $danger?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  color: ${({ $danger, theme }) => {
    if ($danger) return '#ef4444';
    return theme.mode === 'dark' ? '#9ca3af' : '#6b7280';
  }};
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: ${({ $danger, theme }) => {
    if ($danger) return 'rgba(239, 68, 68, 0.1)';
    return theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)';
  }};
    color: ${({ $danger, theme }) => {
    if ($danger) return '#dc2626';
    return theme.mode === 'dark' ? '#f3f4f6' : '#111827';
  }};
  }
`;

export const fileNameFromUrl = (url: string): string => {
  const last = url.replace(/[#?].*$/, '').replace(/\/+$/, '').split('/').pop() || '';
  try {
    return decodeURIComponent(last);
  } catch {
    return last;
  }
};

interface AttachedFileChipProps {
  url: string;
  /** Domyślnie PDF; dla linków ikona globusa i pełny adres zamiast nazwy pliku. */
  kind?: 'pdf' | 'url';
  onRemove?: () => void;
  /** Bez ramki: gdy chip stoi już w wierszu z własną ramką (np. LanguageFileUpload). */
  bare?: boolean;
  className?: string;
}

/** Załączony plik w jednej linii: ikona, nazwa z wielokropkiem (link), podgląd i usuń. */
export const AttachedFileChip: FC<AttachedFileChipProps> = ({
  url, kind = 'pdf', onRemove, bare = false, className,
}) => {
  const { t } = useTranslation();
  const name = kind === 'url' ? url.replace(/^https?:\/\//, '') : fileNameFromUrl(url);
  const content = (
    <>
      {kind === 'url'
        ? <Globe size={18} strokeWidth={1.75} style={{ color: '#10b981', flexShrink: 0 }} />
        : <FileText size={18} strokeWidth={1.75} style={{ color: '#9333ea', flexShrink: 0 }} />}
      <Name href={url} target="_blank" rel="noreferrer" title={name}>{name}</Name>
      <IconButton type="button" onClick={() => window.open(url, '_blank')} aria-label={t('common.actions.preview', 'Podgląd')}>
        <ExternalLink size={15} />
      </IconButton>
      {onRemove && (
        <IconButton type="button" $danger onClick={onRemove} aria-label={t('common.delete', 'Usuń')}>
          <Trash2 size={15} />
        </IconButton>
      )}
    </>
  );
  if (bare) {
    return <div className={className} style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>{content}</div>;
  }
  return <Chip className={className}>{content}</Chip>;
};
